<template>
  <v-row>
    <v-col cols="12" lg="10" offset-lg="1">
      <v-breadcrumbs :items="breadcrumbs" divider=">" class="pl-1" />
      <v-card>
        <v-card-text class="theme">
          <v-row>
            <v-col cols="12" sm="6" md="2" lg="2" class="hidden-md-and-down">
              <v-btn 
                v-if="employee.profile_image_url" 
                height="120" 
                icon
                width="120"
                @click="onFilePick">
                <input
                  type="file"
                  style="display: none"
                  ref="fileInput"
                  accept="*/*"
                  @change="onFilePicked">
                <v-hover>
                  <template v-slot:default="{ hover }"> 
                    <v-avatar
                      size="120"
                      hover 
                    >
                      <v-fade-transition>
                        <v-overlay
                          v-if="hover"
                          absolute
                          color= "grey darken-4"
                        >
                          <div text>Upload</div>
                        </v-overlay>
                      </v-fade-transition>
                      <v-img :src="employee.profile_image_url"></v-img>
                    </v-avatar>
                  </template>
                </v-hover>
              </v-btn>
          <v-btn v-else icon height="120" width="120"
            @click="onFilePick">
            <input
                  type="file"
                  style="display: none"
                  ref="fileInput"
                  accept="*/*"
                  @change="onFilePicked">
              <v-hover>
                <template v-slot:default="{ hover }"> 
                  <v-avatar
                    size="120"
                    dark
                    class="grey lighten-2 my-1 display-3"
                    :style="{ color: getColorFromName(`${employee.id}`) }"
                  >
                    {{ getNameInitials(`${employee.first_name} ${employee.last_name}`) }}
                    <v-fade-transition>
                      <v-overlay
                        v-if="hover"
                        absolute
                        color= "black"
                        class="subtitle-1"
                      >
                        <div text>Upload</div>
                      </v-overlay>
                    </v-fade-transition>
                  </v-avatar>
                </template>
              </v-hover>
            </v-btn>
          </v-col>
            <v-col cols="12" sm="12" md="6" lg="4">
              <div class="title font-weight-regular white--text">
                {{ employee.first_name }} {{ employee.last_name }}
              </div>
              <div class="subtitle-2 font-weight-regular white--text">
                {{ employee.job_title }}
              </div>
              <div class="subtitle-2 font-weight-regular white--text">
                {{ employee.work_email }}
              </div>
              <div class="subtitle-2 font-weight-regular white--text">
                {{
                  employee.work_phone_isd
                    ? employee.work_phone_isd.dial_code
                    : ""
                }}
                {{ employee.work_phone_num }}
              </div>
            </v-col>

            <v-col cols="12" sm="12" class="my-0 py-0">
              <v-tabs
                dark
                slider-color="white"
                background-color="#551738"
                v-model="tabModel"
                show-arrows
              >
                <v-tab href="#timeline" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">timeline</v-icon>
                  History
                </v-tab>

                <v-tab href="#work" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">business</v-icon>
                  Work
                </v-tab>
                <v-tab href="#personal" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">account_circle</v-icon>
                  Personal
                </v-tab>
                <v-tab href="#bank" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">account_balance</v-icon>
                  Payroll
                </v-tab>

                <v-tab href="#document" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">assignment</v-icon>
                  Docs
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text>
          <v-tabs-items v-model="tabModel">
            <v-tab-item :value="'timeline'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">A quick overview of your working history at the company. Good for record keeping of updates and changes.</div>
                <v-card-text>
                  <v-timeline align-top dense v-if="employeeTimeline">
                    <v-timeline-item
                      color="action"
                      v-for="item in employeeTimeline"
                      :key="item.id"
                      icon="timeline"
                    >
                      <v-row class="mt-n3">
                        <v-col md="2" sm="4">
                          <strong>{{ item.date.substring(0, 10) }}</strong>
                        </v-col>
                        <v-col md="10" sm="8">
                          <strong>{{ item.title }}</strong>
                          <div>{{ item.description }}</div>
                        </v-col>
                      </v-row>
                    </v-timeline-item>
                  </v-timeline>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="'work'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">Info about your current position, salary, and reporting manager. Updated by the company.</div>
                <v-card-text>
                  <v-form ref="workForm" v-model="workFormValidation">
                    <v-container>
                      <v-row>
                        <v-col class="xs" cols="12" lg="5">
                          <v-text-field
                            class="my-3"
                            label="Job title*"
                            placeholder="Example: Software Engineer"
                            :rules="[validationRules.required]"
                            :disabled="workDisable"
                            persistent-hint
                            v-model="employee.job_title"
                          />
                           <v-text-field
                          class="my-3"
                          disabled
                          persistent-hint
                          hint="Account activation email will be sent to this email"
                          placeholder="Example: employee@company.com"
                          label="Company email"
                          :rules="[
                            validationRules.required,
                            validationRules.email,
                          ]"
                          v-model="personal.email"
                          type="email"
                        />
                          <v-autocomplete
                            class="my-3"
                            placeholder="Example: Oslo"
                            label="Work location"
                            persistent-hint
                            :items="countries"
                            item-text="name"
                            item-value="name"
                            :disabled="workDisable"
                            v-model="employee.job_location"
                          />
                          <v-dialog
                            ref="dialog2"
                            v-model="startDateModal"
                            :return-value.sync="employee.start_date"
                            persistent
                            :disabled="workDisable"
                            width="290px"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                persistent-hint
                                label="Job start date"
                                :rules="[validationRules.required]"
                                v-model="computedStartDateFormatted"
                                readonly
                                :disabled="workDisable"
                                placeholder="Example: 31-12-2020"
                                v-on="on"
                              />
                            </template>
                            <v-date-picker
                              v-model="employee.start_date"
                              scrollable
                              class="mt-0 pt-0"
                              :first-day-of-week="1"
                            >
                              <v-btn
                                text
                                small
                                class="theme-text"
                                @click="startDateModal = false"
                                >Cancel
                              </v-btn>
                              <v-spacer></v-spacer>
                              <v-btn
                                text
                                small
                                class="theme-text"
                                @click="$refs.dialog2.save(employee.start_date)"
                                >OK
                              </v-btn>
                            </v-date-picker>
                          </v-dialog>

                          <v-select
                            class="my-3"
                            :items="employmentTypes"
                            persistent-hint
                            :rules="[validationRules.required]"
                            v-model="employee.employment_type"
                            :disabled="workDisable"
                            label="Employment type*"
                            placeholder="Example: Permanent"
                          />
                          <v-text-field
                            class="my-3"
                            label="Employment percentage"
                            persistent-hint
                            placeholder="Example: 100"
                            :disabled="workDisable"
                            hint="100% = full time employee"
                            v-model.number="employee.percentage_employed"
                            type="number"
                            :rules="[validationRules.percentageEmployedRule]"
                            suffix="%"
                          />
                        </v-col>
                        <v-col class="xs" cols="12" lg="7" />
                        <v-col class="xs" cols="12" lg="5">
                          <v-select
                            class="my-2"
                            placeholder="Example: Accounts"
                            :items="departments"
                            hint="You can add departments in company settings"
                            :rules="[validationRules.required]"
                            v-model="employee.department"
                            :disabled="workDisable"
                            persistent-hint
                            label="Department*"
                            item-text="name"
                            item-value="name"
                          />
                        </v-col>
                        <v-col class="xs" cols="12" lg="7">
                          <div class="ml-4 mt-1 grey--text">
                            If you do not see any value here, please go to
                            <router-link
                              :to="{ name: 'ListDepartments' }"
                              style="text-decoration: none"
                              >Department settings
                            </router-link>
                            and add departments.
                          </div>
                        </v-col>
                        <v-col class="xs" cols="12" lg="5">
                          <v-autocomplete
                            class="my-3"
                            label="Manager*"
                            :rules="[validationRules.required]"
                            persistent-hint
                            :disabled="workDisable"
                            hint="Reporting manager for the employee"
                            :items="managers"
                            :item-text="managerName"
                            v-model="employee.manager"
                            return-object
                            placeholder="Example: Frank Davis"
                          >
                            <template v-slot:append-outer>
                              <v-tooltip right>
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    small
                                    class="grey--text pt-1"
                                    v-on="on"
                                    >help_outline
                                  </v-icon>
                                </template>
                                This will be the HR by default. In future we
                                will add more
                                <br />roles to accommodate more fine grained
                                controls.
                              </v-tooltip>
                            </template>
                          </v-autocomplete>

                          <v-text-field
                            class="my-3"
                            label="Salary"
                            persistent-hint
                            placeholder="Example: 30000"
                            :mask="masks.salary"
                            :disabled="workDisable"
                            hint="Annual salary of the employee"
                            v-model="employee.salary"
                          />

                          <v-select
                            class="my-3"
                            :items="payFrequencies"
                            hint="Monthly/ BiWeekly/ Annual"
                            v-model="employee.pay_frequency"
                            persistent-hint
                            placeholder="Example: Monthly"
                            :disabled="workDisable"
                            label="Payment Frequency*"
                          />
                        </v-col>
                        <!-- <v-flex xs12 lg7 /> -->
                      </v-row>
                      <v-btn
                        depressed
                        class="save rounded-sm white--text ml-0"
                        :disabled="workDisable"
                        @click="saveWorkInfo"
                        >Save
                      </v-btn>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!--personal + address tab-->
            <v-tab-item :value="'personal'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">Your own corner that you should keep updated. Important stuff that the company needs to know about you.</div>
                <v-card-text>
                  <v-form ref="personalForm" v-model="personalInfoValidator">
                    <v-row>
                      <v-col cols="12" lg="5">
                        <div class="theme-text my-3 title">Personal</div>
                        <v-text-field
                          label="First name*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          placeholder="Example: John"
                          :disabled="temporaryDisable"
                          v-model="personal.first_name"
                          class="my-3"
                        />
                        <v-text-field
                          class="my-3"
                          label="Last name"
                          placeholder="Example: Doe"
                          persistent-hint
                          v-model="personal.last_name"
                          :disabled="temporaryDisable"
                        />
                        <v-row>
                          <v-col cols="12" lg="5" md="5">
                            <v-autocomplete
                              class="my-3"
                              label="Phone prefix (country)"
                              placeholder="Example: Norway"
                              :rules="[validationRules.required]"
                              persistent-hint
                              :items="countries"
                              :item-text="isdCodeName"
                              v-model="personal.phone_isd"
                              :return-object="true"
                              :disabled="temporaryDisable"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Select the country for the phone number.
                                  <br />We will automatically fetch the correct
                                  country code.
                                </v-tooltip>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" lg="7" md="7">
                            <v-text-field
                              class="my-3"
                              label="Mobile number"
                              placeholder="Example: 90909090"
                              :rules="[
                                validationRules.required,
                                phoneNumValidator,
                              ]"
                              v-model="personal.phone_num"
                              :disabled="temporaryDisable"
                              validate-on-blur
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Mobile number. Please DO NOT add the country
                                  code
                                  <br />For ex: use 90909090 instead of
                                  +4790909090
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                        <v-dialog
                          ref="dialog"
                          v-model="modal"
                          :return-value.sync="personal.date_of_birth"
                          persistent
                          width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              persistent-hint
                              label="Date of birth"
                              :rules="[validationRules.required]"
                              v-model="computedDateFormatted"
                              readonly
                              placeholder="Example: 31-12-2020"
                              v-on="on"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  We will send you notifications on upcoming
                                  birthdays using this field.
                                  <br />Should be provided in dd-mm-yyyy format.
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="personal.date_of_birth"
                            scrollable
                            class="mt-0 pt-0"
                            :first-day-of-week="1"
                          >
                            <v-btn
                              text
                              small
                              class="theme-text"
                              @click="modal = false"
                              >Cancel</v-btn
                            >
                            <v-spacer />
                            <v-btn
                              text
                              small
                              class="theme-text"
                              @click="$refs.dialog.save(personal.date_of_birth)"
                              >OK</v-btn
                            >
                          </v-date-picker>
                        </v-dialog>
                        <v-select
                          class="my-3"
                          :items="genders"
                          v-model="personal.gender"
                          persistent-hint
                          :disabled="temporaryDisable"
                          label="Gender"
                          placeholder="Example: Female"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <v-icon small class="grey--text pt-1" v-on="on"
                                  >help</v-icon
                                >
                              </template>
                              We use this field to generate anonymous
                              statistics.
                            </v-tooltip>
                          </template>
                        </v-select>

                        <v-text-field
                          class="my-3"
                          label="Social security number*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          placeholder="Example: 01129955131"
                          v-model="personal.ssn"
                          :disabled="temporaryDisable"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <v-icon small class="grey--text pt-1" v-on="on"
                                  >help</v-icon
                                >
                              </template>
                              In future, we will use this field to automatically
                              generate employee
                              <br />contracts and other documents, which you can
                              use for official purposes.
                            </v-tooltip>
                          </template>
                        </v-text-field>
                        <v-autocomplete
                          class="my-3"
                          label="Nationality"
                          placeholder="Example: Norway"
                          persistent-hint
                          :items="countries"
                          item-text="name"
                          item-value="name"
                          v-model="personal.nationality"
                          :return-object="true"
                          :disabled="temporaryDisable"
                            >
                        </v-autocomplete>
                        <v-select
                          class="my-3"
                          :items="sizes"
                          v-model="personal.size"
                          persistent-hint
                          :disabled="temporaryDisable"
                          label="T-shirt size"
                          placeholder="Example: S"
                        >
                        </v-select>
                        <v-text-field
                          class="my-3"
                          label="Allergies"
                          v-model="personal.allergies"
                          :disabled="temporaryDisable"
                        />
                        <v-text-field
                          class="my-3"
                          label="Food preference"
                          v-model="personal.food_preference"
                          :disabled="temporaryDisable"
                        />
                        <v-textarea
                          class="my-3"
                          label="Personal interests & hobbies"
                          v-model="personal.interests"
                          :disabled="temporaryDisable"
                        />
                        <div class="theme-text my-5 title">
                          Emergency contact
                        </div>
                        <v-text-field
                          label="Emergency contact name"
                          persistent-hint
                          class="my-3"
                          v-model="personal.emergency_contact_name"
                          :disabled="temporaryDisable"
                          placeholder="Example: Jane Doe"
                        />

                        <v-text-field
                          label="Emergency contact email"
                          class="my-3"
                          :rules="[validationRules.email]"
                          :disabled="temporaryDisable"
                          placeholder="Example: jane.doe@email.com"
                          v-model="personal.emergency_contact_email"
                        />
                        <v-row>
                          <v-col cols="12" lg="5" md="5">
                            <v-autocomplete
                              label="Phone prefix emergency contact"
                              placeholder="Example: Norway"
                              persistent-hint
                              class="my-3"
                              :disabled="temporaryDisable"
                              :items="countries"
                              :item-text="isdCodeName"
                              :return-object="true"
                              v-model="personal.emergency_contact_phone_isd"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Select the country for the phone number.
                                  <br />We will automatically fetch the correct
                                  country code.
                                </v-tooltip>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" lg="7" md="7">
                            <v-text-field
                              label="Emergency contact mobile"
                              persistent-hint
                              class="my-3"
                              :rules="[validateEmergencyPhoneNumber]"
                              validate-on-blur
                              :disabled="temporaryDisable"
                              placeholder="Example: 90909090"
                              v-model="personal.emergency_contact_phone_num"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Mobile number. Please DO NOT add the country
                                  code
                                  <br />For ex: use 90909090 instead of
                                  +4790909090
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col cols="12" lg="6" class="offset-lg-1">
                        <div class="theme-text my-3 title">Address</div>
                        <v-text-field
                          class="my-3"
                          label="Address line 1*"
                          :disabled="temporaryDisable"
                          :rules="[validationRules.required]"
                          placeholder="Example : Fredlundveien 48"
                          persistent-hint
                          v-model="personal.line1"
                        />

                        <v-text-field
                          class="my-3"
                          label="Address line 2"
                          v-model="personal.line2"
                          :disabled="temporaryDisable"
                        />

                        <v-text-field
                          class="my-3"
                          label="City*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          :disabled="temporaryDisable"
                          placeholder="Example: Bergen"
                          v-model="personal.city"
                        />
                        <v-text-field
                          class="my-3"
                          label="Postcode*"
                          :rules="[validationRules.required]"
                          :disabled="temporaryDisable"
                          persistent-hint
                          placeholder="Example : 3122"
                          v-model="personal.post_code"
                        />

                        <v-text-field
                          class="my-3"
                          label="State"
                          persistent-hint
                          placeholder="Example: Bergen"
                          :disabled="temporaryDisable"
                          v-model="personal.state"
                        />

                        <v-autocomplete
                          class="my-3"
                          label="Country*"
                          :rules="[validationRules.required]"
                          :disabled="temporaryDisable"
                          persistent-hint
                          placeholder="Example: Norway"
                          :items="countries"
                          item-text="name"
                          item-value="name"
                          v-model="personal.country"
                        />
                      </v-col>
                      <v-btn
                        depressed
                        class="save rounded-sm white--text ml-0"
                        :disabled="temporaryDisable"
                        @click="savePersonal"
                        >Save
                      </v-btn>
                      <v-col cols="12" lg="7" />
                    </v-row>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="'bank'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">Info regarding your salary and which salary account number you’d like to use.</div>
                <v-card-text>
                  <v-form ref="bankForm" v-model="bankFormValidation">
                    <v-container>
                      <v-row>
                        <v-col class="xs" cols="12" lg="5">
                          <v-text-field
                            class="my-3"
                            label="Bank name*"
                            :rules="[validationRules.required]"
                            persistent-hint
                            :disabled="temporaryDisable"
                            placeholder="Example: DNB"
                            v-model="employee.bank_name"
                          ></v-text-field>

                          <v-text-field
                            class="my-3"
                            placeholder="Example: 010101010101"
                            label="Bank account number*"
                            :rules="[validationRules.required]"
                            persistent-hint
                            :disabled="temporaryDisable"
                            hint="Bank account number for salary"
                            v-model="employee.bank_account_number"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-btn
                        depressed
                        class="save rounded-sm white--text ml-0"
                        :disabled="temporaryDisable"
                        @click="saveBankInfo"
                        >Save
                      </v-btn>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="'document'">
              <Documents />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-col>

    <v-snackbar v-model="snackbar" :bottom="true" :right="true">
      {{ snackbarMessage }}
      <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-row>
</template>

<script>
import libphonenumber from "google-libphonenumber";
import { mapGetters } from "vuex";

import { Actions } from "@/views/actions";
import { Breadcrumbs } from "@/views/breadcrumbs";
import { Component } from "@/views/component";
import { Constants } from "@/views/constants";
import Documents from "@/views/protected/employee/Documents";

export default {
  name: Component.Protected.Employee.Me,
  components: { Documents },
  computed: {
    ...mapGetters([
      "userInfo",
      "countries",
      "employeeTimeline",
      "companyInfo",
      "profileImageURL",
      "me"
    ]),
    computedStartDateFormatted() {
      return this.datePickerFormat(this.employee.start_date);
    },
    computedDateFormatted() {
      return this.datePickerFormat(this.personal.date_of_birth);
    },
    phoneNumValidator() {
      if (!this.personal.phone_num)
        return Constants.PhoneNumber.Messages.Error.Required;
      if (isNaN(this.personal.phone_num))
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.personal.phone_num.length < 8)
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (!this.personal.phone_isd || !this.personal.phone_isd.code)
        return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;

      const num = this.phoneValidator.parseAndKeepRawInput(
        this.personal.phone_num,
        this.personal.phone_isd.code
      );
      if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.phoneValidator.isValidNumber(num)) return true;
      return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    },
    validateEmergencyPhoneNumber() {
      // if none then true
      if (
        !this.personal.emergency_contact_phone_isd &&
        !this.personal.emergency_contact_phone_num
      ) {
        return true;
      }
      // Both ISD and Num should be filled
      if (
        this.personal.emergency_contact_phone_isd &&
        !this.personal.emergency_contact_phone_num
      )
        return Constants.PhoneNumber.Messages.Error.Required;
      if (
        !this.personal.emergency_contact_phone_isd &&
        this.personal.emergency_contact_phone_num
      )
        return Constants.PhoneNumber.Messages.Error.SelectISD;
      if (isNaN(this.personal.emergency_contact_phone_num))
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (
        this.personal.emergency_contact_phone_num &&
        this.personal.emergency_contact_phone_num.length < 8
      )
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (
        !this.personal.emergency_contact_phone_num ||
        !this.personal.emergency_contact_phone_isd.code
      )
        return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
      const num = this.phoneValidator.parseAndKeepRawInput(
        this.personal.emergency_contact_phone_num,
        this.personal.emergency_contact_phone_isd.code
      );
      if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.phoneValidator.isValidNumber(num)) return true;
      return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    },
    // workPhoneValidator() {
    //   // Both ISD and Num should be filled
    //   if (this.employee.work_phone_isd && !this.employee.work_phone_num)
    //     return Constants.PhoneNumber.Messages.Error.Required;
    //   if (!this.employee.work_phone_isd && this.employee.work_phone_num)
    //     return Constants.PhoneNumber.Messages.Error.SelectISD;
    //   if (isNaN(this.employee.work_phone_num))
    //     return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   if (
    //     this.employee.work_phone_num &&
    //     this.employee.work_phone_num.length < 8
    //   )
    //     return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   if (!this.employee.work_phone_num || !this.employee.work_phone_isd.code) {
    //     return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    //   }

    //   const num = this.phoneValidator.parseAndKeepRawInput(
    //     this.employee.work_phone_num,
    //     this.employee.work_phone_isd.code
    //   );
    //   if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   if (this.phoneValidator.isValidNumber(num)) return true;
    //   return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    // },
  },
  data: () => ({
    tabModel: "",
    fileInput: null,
    modal: false,
    startDateModal: false,
    phoneNumberValidator: null,
    breadcrumbs: Breadcrumbs.Employee.Me,

    // form values
    genders: [],
    officeTypes: [],
    employmentTypes: [],
    payFrequencies: [],
    departments: [],
    managers: [],
    manager: null,
    sizes: [],

    // personal info
    phoneNUM: null,
    email: null,
    gender: Constants.Employee.Genders.NotDisclosed,
    id: null,
    type: null,
    isHR: null,
    isEmployee: null,
    isManager: null,

    // form validation
    personalInfoValidator: true,
    workFormValidation: true,
    bankFormValidation: true,
    temporaryDisable: false,
    workDisable: false,

    employee: {
      // personal info
      first_name: "",
      last_name: "",
      date_of_birth: "",
      work_email: "",
      phone_isd: "",
      phone_num: "",
      is_employee: null,
      is_hr: null,
      is_manager: false,
      type: "",
      profile_image_url: "",

      // work related information
      work_phone_num: "",
      work_phone_isd: "",
      start_date: "",
      end_date: "",
      employment_type: "",
      job_title: "",
      job_location: "",
      department: "",
      manager: "",
      salary: null,
      pay_frequency: "",
      percentage_employed: null,

      // banking information
      bank_account_number: "",
      bank_name: "",
      tnc_complete: false,
    },

    //Personal profile
    personal: {
      user_id: null,
      email: null,
      phone_isd: null,
      phone_num: null,
      gender: null,
      first_name: null,
      last_name: null,
      date_of_birth: null,
      ssn: null,
      size: null,
      allergies: null,
      food_preference: null,
      interests: null,
      nationality: null,

      //Personal emergency
      emergency_contact_name: null,
      emergency_contact_phone_isd: null,
      emergency_contact_phone_num: null,
      emergency_contact_email: null,
      //Personal address
      address_id: null,
      city: null,
      line1: null,
      line2: null,
      state: null,
      country: null,
      post_code: null,
      type: null,
    },
    fileName: "",
    fileObject: null,
  }),
  methods: {
    onFilePick () {
      this.$refs.fileInput.click()
    },
   onFilePicked (event) {
      const files = event.target.files
      if (files[0] !== undefined) {
        this.fileName = files[0].name
        // Check validity of file
        if (this.fileName.lastIndexOf('.') <= 0) {
          return
        }
        // If valid, continue
        const fr = new FileReader()
        fr.readAsDataURL(files[0])
        fr.addEventListener('load', () => {
          this.fileObject = files[0]
           this.saveImage() 
        })
       
      } else {
        this.fileName = ''
        this.fileObject = null
      }
    },    
    isdCodeName(country) {
      if (country) {
        return `${country.dial_code} - ${country.name}`;
      }
    },
    managerName(item) {
      const fName = item && item.first_name ? item.first_name : "";
      const lName = item && item.last_name ? item.last_name : "";
      return `${fName} ${lName}`;
    },
    saveImage() {
        if (this.fileObject){
        let fileExt = this.fileObject.name.split(".");
        fileExt = fileExt[fileExt.length - 1];
        const fileName = `company/${this.userInfo.namespace}/${this.userInfo.uid}/profile/${this.userInfo.uid}.${fileExt}`;
        const data = {
          fileName: fileName,
          action: 'employeeSave',
          metadata: {
            customMetadata: {
              namespace: this.userInfo.namespace,
              uid: this.userInfo.uid,
              type: "image",
            },
          },
          payload: {
            id: this.userInfo.userID,
            data: {
              profile_image_url: "",
              id: parseInt(this.id),
              tnc_complete: this.employee.tnc_complete,
            },
          },
          content: this.fileObject,
        };

        this.$store
          .dispatch(Actions.Firebase.UploadImage, data)
          .then(() => {
            this.getMe();
          })
          .catch((err) => {
            console.error("could not upload image", err);
            this.snackbar = true;
            this.snackbarMessage = "You can only upload image with size < 3mb";
          })
          .finally(() => {
          });
        } else{
          return false
        }
    },
    getISDStuff(countryCode) {
      return this.countries.filter((e) => e.dial_code === countryCode)[0];
    },
    resetModels() {
      this.personalInfoValidator = true;
      this.workFormValidation = true;
      this.bankFormValidation = true;
      this.temporaryDisable = false;
    },
    getHeaders() {
      return { "id-token": this.userInfo.token };
    },
    getManagers() {
      let payload = { status: Constants.Employee.Type.Active, is_hr: true };
      this.$store.dispatch(Actions.Employee.Find, payload).then((r) => {
        if (r.status === 200) this.managers = r.data;
      });
    },

    getMe() {
      this.$store.dispatch(Actions.Employee.Me).then(() => {
        const today = new Date().toISOString().substring(0, 10);
        const sd = this.isDefaultDate(this.me.start_date)
          ? today
          : this.me.start_date
          ? this.me.start_date.substring(0, 10)
          : today
        const ed = this.isDefaultDate(this.me.end_date)
          ? null
          : this.me.end_date
          ? this.me.end_date.substring(0, 10)
          : null
        this.employee = {
          ...this.me,
          phone_isd: this.getISDStuff(this.me.phone_isd),
          work_email: this.me.work_email ? this.me.work_email : this.me.email,
          start_date: sd,
          end_date: ed ? ed : "",
          work_phone_isd: this.getISDStuff(this.me.work_phone_isd),
        };
        this.id = this.me.id;
        this.isEmployee = this.me.is_employee;
        this.isManager = this.me.is_manager;
        this.isHR = this.me.is_hr;
        this.manager = this.me.manager;
        !this.isHR ? (this.workDisable = true) : (this.workDisable = false);
        })    
      },
    getPersonal() {
      this.$store
        .dispatch(Actions.Employee.MePersonal)
        .then((r) => {
          if (r.status === 200 && r.data) {
            //Profile
            this.personal.user_id = r.data.profile_info.user_id;
            this.personal.email = r.data.profile_info.work_email
              ? r.data.profile_info.work_email
              : r.data.profile_info.email;
            this.personal.first_name = r.data.profile_info.first_name;
            this.personal.last_name = r.data.profile_info.last_name;
            this.personal.phone_isd = this.getISDStuff(
              r.data.profile_info.phone_isd
            );
            this.personal.phone_num = r.data.profile_info.phone_num;
            let dob = r.data.profile_info.date_of_birth;
            if (!dob) {
              this.personal.date_of_birth = null;
            } else if (dob.substring(0, 10) === "0001-01-01") {
              this.personal.date_of_birth = null;
            } else {
              this.personal.date_of_birth = dob.substring(0, 10);
            }
            this.personal.gender = r.data.profile_info.gender;
            this.personal.ssn = r.data.profile_info.ssn_number;
            this.personal.size = r.data.profile_info.size;
            this.personal.allergies = r.data.profile_info.allergies;
            this.personal.food_preference = r.data.profile_info.food_preference;
            this.personal.interests = r.data.profile_info.interests;
            this.personal.nationality = r.data.profile_info.nationality;
            //Emergency
            this.personal.emergency_contact_name =
              r.data.emergency_info.emergency_contact_name;
            this.personal.emergency_contact_phone_isd = this.getISDStuff(
              r.data.emergency_info.emergency_contact_phone_isd
            );
            this.personal.emergency_contact_phone_num =
              r.data.emergency_info.emergency_contact_phone_num;
            this.personal.emergency_contact_email =
              r.data.emergency_info.emergency_contact_email;
            //Address
            this.personal.address_id = r.data.address_info.address_id;
            this.personal.line1 = r.data.address_info.line_1;
            this.personal.line2 = r.data.address_info.line_2;
            this.personal.city = r.data.address_info.city;
            this.personal.state = r.data.address_info.state;
            this.personal.post_code = r.data.address_info.post_code;
            this.personal.country = r.data.address_info.country;
            this.personal.type = r.data.address_info.type;
          }
        })
        .catch((err) => {
          console.error(Constants.Employee.Messages.Error.Get, err);
          this.snackbarMessage = Constants.Employee.Messages.Error.Get;
          this.snackbar = true;
        });
    },

    getInfo() {
      this.$store
        .dispatch(Actions.Employee.Info)
        .then((r) => {
          if (r.status === 200 && r.data) {
            this.genders = r.data.genders;
            this.employmentTypes = r.data.employment_types;
            this.payFrequencies = r.data.payment_frequencies;
            this.officeTypes = r.data.address_types;
            this.departments = r.data.departments;
            this.sizes = r.data.sizes;
          }
        })
        .catch(() => {
          this.snackbarMessage = "Could not get additional info";
          this.snackbar = true;
          this.genders = Constants.Employee.Genders.All;
          this.employmentTypes = Constants.Employee.EmploymentType.All;
          this.payFrequencies = Constants.Employee.PaymentFrequencies.All;
          this.officeTypes = Constants.Employee.AddressType.All;
          this.departments = [];
          this.sizes = Constants.Employee.Sizes.All;
        });
    },
    saveBankInfo() {
      if (this.$refs.bankForm.validate()) {
        let data = {
          bank_name: this.employee.bank_name ? this.employee.bank_name : "",
          bank_account_number: this.employee.bank_account_number
            ? this.employee.bank_account_number
            : "",
          id: parseInt(this.id),
          tnc_complete: this.employee.tnc_complete,
        };
        if (!data.bank_name && !data.bank_account_number) {
          this.temporaryDisable = false;
          return false;
        }
        this.temporaryDisable = true;
        this.$store
          .dispatch(Actions.Employee.Save, {
            id: this.userInfo.userID,
            data: data,
          })
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Bank;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.temporaryDisable = false;
          });
      }
    },

    saveWorkInfo() {
      if (this.$refs.workForm.validate()) {
        const data = {
          start_date: this.computedStartDateFormatted
            ? this.computedStartDateFormatted
            : null,
          employment_type: this.employee.employment_type
            ? this.employee.employment_type
            : "",
          job_title: this.employee.job_title ? this.employee.job_title : "",
          job_location: this.employee.job_location
            ? this.employee.job_location
            : "",
          department: this.employee.department ? this.employee.department : "",
          manager: this.managerName(this.employee.manager),
          manager_id: this.employee.manager
            ? parseInt(this.employee.manager.id)
            : null,
          pay_frequency: this.employee.pay_frequency
            ? this.employee.pay_frequency
            : "",
          id: parseInt(this.id),
          salary: this.employee.salary ? parseFloat(this.employee.salary) : 0.0,
          percentage_employed: this.employee.percentage_employed
            ? parseInt(this.employee.percentage_employed)
            : 0,
          tnc_complete: this.employee.tnc_complete
        };
        this.workDisable = true;
        this.$store
          .dispatch(Actions.Employee.Save, {
            id: this.userInfo.userID,
            data: data,
          })
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Work;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.workDisable = false;
          });
      }
    },
    savePersonal() {
      if (this.$refs.personalForm.validate()) {
        let profile_info = {
          user_id: parseInt(this.personal.user_id),
          email: this.personal.email,
          first_name: this.personal.first_name,
          last_name: this.personal.last_name,
          date_of_birth: this.computedDateFormatted
            ? this.computedDateFormatted
            : null,
          phone_isd: this.personal.phone_isd.dial_code,
          phone_num: this.personal.phone_num,
          gender: this.personal.gender,
          ssn_number: this.personal.ssn,
          size: this.personal.size,
          allergies: this.personal.allergies,
          food_preference: this.personal.food_preference,
          interests: this.personal.interests,
          nationality: this.personal.nationality.name,
          
        };
        let emergency_info = {
          emergency_contact_name: this.personal.emergency_contact_name,
          emergency_contact_phone_isd:
            this.personal.emergency_contact_phone_isd &&
            this.personal.emergency_contact_phone_isd.dial_code
              ? this.personal.emergency_contact_phone_isd.dial_code
              : "",
          emergency_contact_phone_num: this.personal
            .emergency_contact_phone_num,
          emergency_contact_email: this.personal.emergency_contact_email,
        };
        let address_info = {
          address_id: parseInt(this.personal.address_id),
          type: this.personal.type
            ? this.personal.type
            : Constants.Employee.AddressType.Home,
          line_1: this.personal.line1,
          line_2: this.personal.line2,
          city: this.personal.city,
          state: this.personal.state,
          post_code: this.personal.post_code,
          country: this.personal.country,
        };
        this.tempDisable = true;
        let payload = {
          profile_info,
          address_info,
          emergency_info,
        };
        this.$store
          .dispatch(Actions.Employee.MePersonalUpdate, payload)
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Personal;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.tempDisable = false;
            this.getPersonal();
            this.getMe();
          });
      }
    },
    getTimeline() {
      this.$store
        .dispatch(Actions.Employee.Timeline, this.userInfo.userID)
        .catch((err) => {
          console.error(Constants.Employee.Messages.Error.Timeline, err);
          this.snackbarMessage = Constants.Employee.Messages.Error.Timeline;
          this.snackbar = true;
        });
    },
    setup() {     
      this.phoneValidator = libphonenumber.PhoneNumberUtil.getInstance();
      this.getInfo();
      this.getManagers();
      this.getMe();
      this.getTimeline();
      this.getPersonal();
    },
  },
  created() {
    this.resetModels();
    if (this.userInfo && this.userInfo.token) {
      this.setup();
    } else {
      this.$store.dispatch(Actions.Firebase.UserInfo).then(() => this.setup());
    }
  },
};
</script>
<style scoped>
* >>> div.v-picker__title {
  background-color: #551738 !important;
  border-color: #551738 !important;
}

* >>> div.v-item-group {
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}

* >>> div.v-slide-group__next > i,
* >>> div.v-slide-group__prev > i {
  color: #eceff1 !important;
}
</style>
